import { useState, useEffect } from 'react';
import {
  getAIAssistantProfiles, getAIAssistantProfile, createAIAssistantProfile, updateAIAssistantProfile,
  requestActivateAIAssistantProfile, approveAIAssistantProfile, rejectAIAssistantProfile,
} from '../api';
import { useAuth } from '@/hooks/useAuth';
import type { AIAssistantProfileSummary, AIAssistantProfile } from '../types';

const DOC_TYPE_GROUPS = [
  { key: 'OPD Prescription', label: 'OPD Prescription' },
  { key: 'Consultation Notes', label: 'Consultation Notes' },
  { key: 'Discharge Summary', label: 'Discharge Summary' },
  { key: 'Referral Letter', label: 'Referral Letter (Emergency)' },
  { key: 'All', label: 'General-purpose (fallback for any type without its own active profile)' },
];

// Referral Letter works differently from the other types (backend
// utils/aiAssistantProfile.js NO_GENERAL_FALLBACK, utils/erDocuments.js): its
// text is ADDED to the Emergency Department's built-in prompt as the
// hospital's instructions — the ED's safety rules (recorded facts only, no
// invented doses or results) still apply — and it never falls back to the
// General-purpose profile, which is written for OPD notes.
const EMPTY_TEXT: Record<string, string> = {
  All: 'any doc type without its own active profile falls back to the app default',
  'Referral Letter': "the Emergency Department's built-in referral letter instructions are used until one is activated here (the General-purpose profile is not used for referral letters)",
};
const EMPTY_DEFAULT = 'falls back to the General-purpose profile (or the app default) until one is activated here';

const REFERRAL_LETTER_STARTER = `You are writing a formal referral letter from our Emergency Department to another doctor or hospital, in the style of an Indian hospital letter.

Style:
- Formal, courteous and brief. Address the receiving doctor respectfully.
- Open with: "Kindly evaluate <patient title and name>, <age>-year-old <gender>, with <diagnosis>."
- Then one paragraph with the clinical summary: presenting complaint and duration, relevant history, vitals and key examination findings, investigation results, and the treatment given in the Emergency Department (medicine, dose, route and time).
- Then state the reason for referral and the patient's condition at the time of referral.
- Close with: "Kindly assess the patient for further management, including <recommended evaluation or treatment>."

Do not write the letterhead, date, "To" address, salutation, "Thanking you" or signature — the hospital system prints those.
Write medicine names as generic names. Use plain sentences, no bullet points, no tables.`;

export default function AIAssistantRolesTab() {
  const { user } = useAuth();
  const isReviewer = user?.role === 'Executive Director';
  const [profiles, setProfiles] = useState<AIAssistantProfileSummary[] | null>(null);
  const [editing, setEditing] = useState<AIAssistantProfileSummary | AIAssistantProfile | null>(null);
  const [viewing, setViewing] = useState<AIAssistantProfileSummary | AIAssistantProfile | null>(null);
  const [error, setError] = useState('');
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = () => getAIAssistantProfiles().then(setProfiles).catch((e: any) => setError(e.message));
  useEffect(() => { load(); }, []);

  const requestActivate = async (p: AIAssistantProfileSummary) => {
    setBusyId(p.id!); setError('');
    try { await requestActivateAIAssistantProfile(p.id!); await load(); }
    catch (e: any) { setError(e.message); }
    setBusyId(null);
  };
  const approve = async (p: AIAssistantProfileSummary) => {
    try { await approveAIAssistantProfile(p.id!); await load(); } catch (e: any) { setError(e.message); }
  };
  const reject = async (p: AIAssistantProfileSummary) => {
    try { await rejectAIAssistantProfile(p.id!, rejectNotes.trim()); setRejecting(null); setRejectNotes(''); await load(); } catch (e: any) { setError(e.message); }
  };

  if (editing) {
    return <ProfileEditor profile={editing} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />;
  }
  if (viewing) {
    return <ProfileEditor profile={viewing} onCancel={() => setViewing(null)} onSaved={async () => {}} readOnly />;
  }

  if (!profiles) return <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading AI Assistant Role profiles…</div>;

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, lineHeight: 1.6 }}>
        The active profile's text is used as the AI's system prompt when generating documents in the Consultation stage — its persona and output format fully replace the app's built-in default whenever it is active. A Referral Letter profile is used in the Emergency Department, added to its built-in instructions.
      </div>
      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}

      {DOC_TYPE_GROUPS.map((group) => {
        const groupProfiles = profiles.filter((p) => (p.doc_type || 'All') === group.key);
        return (
          <div key={group.key} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{group.label}</div>
              <button className="btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }}
                onClick={() => setEditing({ name: '', description: '', content: group.key === 'Referral Letter' ? REFERRAL_LETTER_STARTER : '', doc_type: group.key })}>
                + New profile
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {groupProfiles.length === 0 && (
                <div style={{ textAlign: 'center', padding: 18, color: '#9CA3AF', fontSize: 12.5, background: '#F9FAFB', borderRadius: 'var(--radius)' }}>
                  No profile yet — {EMPTY_TEXT[group.key] ?? EMPTY_DEFAULT}
                </div>
              )}
              {groupProfiles.map((p) => (
                <div key={p.id} className="card" style={{ padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                      {p.description && <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{p.description}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className={`badge ${p.active ? 'badge-success' : 'badge-gray'}`}>{p.active ? 'Active' : 'Inactive'}</span>
                      <button className="btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }}
                        onClick={async () => { const full = await getAIAssistantProfile(p.id!); setViewing(full); }}>View</button>
                      <button className="btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }}
                        onClick={async () => { const full = await getAIAssistantProfile(p.id!); setEditing(full); }}>Edit</button>
                      {!p.active && !p.pending_status && (
                        <button className="btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} disabled={busyId === p.id} onClick={() => requestActivate(p)}>
                          {busyId === p.id ? 'Requesting…' : '⚡ Request activation'}
                        </button>
                      )}
                    </div>
                  </div>

                  {p.pending_status === 'pending' && (
                    <div style={{ marginTop: 10, padding: '10px 12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--radius)' }}>
                      <div style={{ fontSize: 12, color: '#92400E' }}>
                        ⏳ {p.pending_action === 'activate' ? 'Activation requested' : 'Pending content changes submitted'} by <b>{p.pending_submitted_by}</b> on {new Date(p.pending_submitted_at!).toLocaleString('en-IN')} — awaiting Executive Director review.
                      </div>
                      {isReviewer && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button className="btn-success" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => approve(p)}>✓ Approve</button>
                          <button className="btn-danger" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => { setRejecting(p.id!); setRejectNotes(''); }}>✕ Reject</button>
                        </div>
                      )}
                      {rejecting === p.id && (
                        <div style={{ marginTop: 8 }}>
                          <textarea placeholder="Reason for rejection…" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                            style={{ resize: 'vertical', height: 60, width: '100%', fontSize: 12, marginBottom: 6 }} />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn-danger" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => reject(p)}>Confirm reject</button>
                            <button className="btn-ghost" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => setRejecting(null)}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {p.pending_status === 'rejected' && (
                    <div style={{ marginTop: 10, padding: '10px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 'var(--radius)', fontSize: 12, color: '#991B1B' }}>
                      ✕ Last request rejected by <b>{p.pending_reviewed_by}</b>{p.pending_review_notes ? `: "${p.pending_review_notes}"` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ProfileEditor({ profile, onCancel, onSaved, readOnly }: { profile: AIAssistantProfileSummary | AIAssistantProfile; onCancel: () => void; onSaved: () => Promise<void>; readOnly?: boolean }) {
  // In read-only (review) mode, show the pending draft under review when one
  // exists — that's what a checker needs to see, not the still-active content.
  const showingPending = !!readOnly && !!profile.pending_content;
  const [name, setName] = useState(profile.name);
  const [description, setDescription] = useState(profile.description || '');
  const [content, setContent] = useState(showingPending ? profile.pending_content! : ('content' in profile ? profile.content || '' : ''));
  const [docType, setDocType] = useState(profile.doc_type || 'All');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isNew = profile.id === undefined;
  const docTypeLocked = !isNew && !!profile.active;

  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  const save = async () => {
    if (!name.trim()) { setError('Profile name is required'); return; }
    if (!content.trim()) { setError('Prompt content is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { name: name.trim(), description: description.trim() || null, content, doc_type: docType };
      if (isNew) await createAIAssistantProfile(payload);
      else await updateAIAssistantProfile(profile.id!, payload);
      await onSaved();
    } catch (e: any) { setError(e.message); setSaving(false); }
  };

  return (
    <div style={{ maxWidth: 860 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{readOnly ? `Viewing: ${profile.name}` : (isNew ? 'New AI Assistant Role profile' : `Editing: ${profile.name}`)}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          {readOnly ? (
            <button className="btn-ghost" onClick={onCancel}>Close</button>
          ) : (
            <>
              <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save profile'}</button>
            </>
          )}
        </div>
      </div>

      {readOnly && showingPending && (
        <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--radius)', fontSize: 12, color: '#92400E' }}>
          ⏳ Showing the pending draft submitted by {profile.pending_submitted_by}, not yet approved.
        </div>
      )}

      {!isNew && profile.active && (
        <div style={{ marginBottom: 14, padding: '8px 12px', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 'var(--radius)', fontSize: 12, color: '#075985' }}>
          ℹ This profile is currently active. Saving here submits your changes for Executive Director review — the currently-approved content keeps driving real consultations until it is approved.
        </div>
      )}

      {!isNew && profile.pending_content && (
        <div style={{ marginBottom: 14, padding: '10px 12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--radius)', fontSize: 12, color: '#92400E' }}>
          ⏳ There is already a pending edit awaiting review (submitted by {profile.pending_submitted_by}). Saving again will replace that pending draft.
        </div>
      )}

      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}

      <div className="card" style={{ padding: 14, marginBottom: 16 }}>
        <div className="grid2">
          <div>
            <label className="lbl">Profile name (e.g. "KMC Consultant AI v1.0")</label>
            <input value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} />
          </div>
          <div>
            <label className="lbl">Description (optional)</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short note on what this variation is for" disabled={readOnly} />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label className="lbl">Document type this profile is for</label>
            <select value={docType} onChange={(e) => setDocType(e.target.value)} disabled={readOnly || docTypeLocked}>
              {DOC_TYPE_GROUPS.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
            {docTypeLocked && (
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                This profile is active, so its document type is locked — deactivate it first if it needs to move to a different type.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <label className="lbl" style={{ margin: 0 }}>Prompt content</label>
          <span style={{ fontSize: 11, color: '#9CA3AF' }}>{wordCount.toLocaleString()} words</span>
        </div>
        <div style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 8, lineHeight: 1.5 }}>
          {docType === 'Referral Letter'
            ? "These are the hospital's instructions for writing a referral letter in the Emergency Department — tone, wording, structure and what to include. They are added to the Emergency Department's own prompt, which already supplies the patient's ED record and keeps its safety rules (only recorded facts, nothing invented). Used by the ED document assistant and by \"✨ Write with AI\" in the referral letter form."
            : "This becomes the AI's entire system prompt when this profile is active — role, writing style, and output format. Patient details (name/vitals/complaints) and the document type are appended automatically at runtime, so there is no need to include them here."}
        </div>
        <textarea value={content} onChange={(e) => setContent(e.target.value)} readOnly={readOnly}
          style={{ width: '100%', height: 440, resize: 'vertical', fontFamily: 'var(--mono)', fontSize: 12.5, lineHeight: 1.6 }} />
      </div>
    </div>
  );
}
